import { get } from '@rover/react-lib/src/apis/globalAnnouncements.api';
import fetchGlobalAnnouncementResponseFactory from '@rover/react-lib/src/factories/fetchGlobalAnnouncementsResponseFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import {
  GlobalAnnouncements,
  PaginatedGlobalAnnouncementsList,
} from '@rover/rsdk/src/apiClient/latest';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';

import generateActions from './generateActions';

export type State = {
  errorMessage: string;
  loading: boolean;
  fetchGlobalAnnouncementsResponse: PaginatedGlobalAnnouncementsList;
};

type Actions = {
  fetchGlobalAnnouncementsPending: ActionCreator<void>;
  fetchGlobalAnnouncementsSuccess: ActionCreator<PaginatedGlobalAnnouncementsList>;
  fetchGlobalAnnouncementsFailure: ActionCreator<string>;
};

type Effects = {
  fetchGlobalAnnouncements: EffectCreator<void>;
};

type Selectors = {
  getGlobalAnnouncements: SelectorCreator<GlobalAnnouncements[]>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'globalAnnouncements',
  initialState: {
    errorMessage: '',
    loading: true,
    fetchGlobalAnnouncementsResponse: fetchGlobalAnnouncementResponseFactory(),
  },
  actions: { ...generateActions('fetchGlobalAnnouncements') },
  effects: {
    fetchGlobalAnnouncements:
      () =>
      async (
        dispatch,
        getState,
        {
          fetchGlobalAnnouncementsPending,
          fetchGlobalAnnouncementsSuccess,
          fetchGlobalAnnouncementsFailure,
        }
      ) => {
        dispatch(fetchGlobalAnnouncementsPending());

        try {
          const fetchGlobalAnnouncementResponse: PaginatedGlobalAnnouncementsList = await get();
          dispatch(fetchGlobalAnnouncementsSuccess(fetchGlobalAnnouncementResponse));
        } catch (e) {
          dispatch(fetchGlobalAnnouncementsFailure((e as Error).message));
          captureError(e as Error);
        }
      },
  },
  selectors: {
    getGlobalAnnouncements: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchGlobalAnnouncementsResponse.results),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
